<template>
  <div class="context-switcher-wrapper">
    <button
      ref="buttonRef"
      @click.stop="toggleDropdown"
      class="context-switcher-btn"
      :class="{ 'active': showDropdown }"
    >
      <!-- The active household's photo, when it has one. This is the only
           always-visible surface in the app, so it is where a household photo
           earns its keep — before this it appeared solely inside the dropdown,
           meaning you saw your own home only while a menu happened to be open.
           Small and squared to the design-system radius: identity, not
           decoration.

           The fallback is the pot dot, not a house pictogram: it is the same
           mark and colour the seam uses everywhere else, so the switcher still
           reads as "which pot is active". --pot already resolves to the active
           context via <html data-pot>, which is also why personal context keeps
           the dot — there is no photo of "mitt", and the brass mark is the
           design. -->
      <AppImage
        v-if="activeHouseholdImageId"
        class="context-thumb"
        :image-id="activeHouseholdImageId"
        :alt="contextLabel"
        aspect="1 / 1"
      />
      <span v-else class="pot-dot" aria-hidden="true"></span>
      <div class="context-label">
        <span>{{ contextLabel }}</span>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
    </button>

  </div>

  <!-- Mobile: Teleport dropdown to body with fixed positioning -->
  <Teleport to="body">
    <!-- Mobile Backdrop -->
    <div v-if="showDropdown && isMobile" class="block fixed inset-0" style="z-index: calc(var(--z-dropdown) - 1); background: rgba(18, 23, 26, 0.3); backdrop-filter: blur(2px);" @click="closeDropdown"></div>

    <!-- Mobile Dropdown Menu -->
    <div
      v-if="showDropdown && isMobile"
      class="fixed max-h-[400px] overflow-y-auto touch-manipulation"
      style="background: var(--surface); border: 2px solid var(--border-default); border-radius: 6px; box-shadow: 0 8px 24px rgba(18, 23, 26, 0.2);"
      :style="dropdownStyle"
      @click.stop
    >
      <!-- Personal Budget -->
      <button
        @click="switchToPersonal"
        class="w-full flex items-center gap-3 px-4 py-3 border-0 cursor-pointer transition-all duration-200 text-left touch-manipulation [-webkit-tap-highlight-color:transparent] min-h-[44px]"
        :style="{ background: isPersonalContext ? 'var(--accent-subtle)' : 'transparent', color: isPersonalContext ? 'var(--accent)' : 'var(--text-primary)' }"
      >
        <span class="pot-dot pot-dot--mitt" aria-hidden="true"></span>
        <span>{{ $t('household.personal_budget') }}</span>
        <svg v-if="isPersonalContext" class="w-5 h-5 shrink-0 ml-auto text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </button>

      <div v-if="households.length > 0" class="my-1" style="height: 1px; background: var(--border-subtle);"></div>

      <!-- Household Budgets -->
      <div v-for="household in households" :key="household.id">
        <button
          @click="switchToHousehold(household.id)"
          class="w-full flex items-center gap-3 px-4 py-3 border-0 cursor-pointer transition-all duration-200 text-left touch-manipulation [-webkit-tap-highlight-color:transparent] min-h-[44px]"
          :style="{ background: isActiveHousehold(household.id) ? 'var(--accent-subtle)' : 'transparent', color: isActiveHousehold(household.id) ? 'var(--accent)' : 'var(--text-primary)' }"
        >
          <!-- The household photo where the dot would be: switching context is
               moving between places, and a face/photo says which one faster
               than a name does. -->
          <AppAvatar
            v-if="household.imageId"
            size="sm"
            :image-id="household.imageId"
            :name="household.name"
            :alt="household.name"
          />
          <span v-else class="pot-dot pot-dot--felles" aria-hidden="true"></span>
          <div class="flex-1 flex flex-col gap-1 min-w-0">
            <span class="truncate">{{ household.name }}</span>
            <span style="font-size: 0.85rem; color: var(--text-tertiary);">{{ household.members.length }} {{ household.members.length === 1 ? $t('household.member') : $t('household.members_word') }}</span>
          </div>
          <svg v-if="isActiveHousehold(household.id)" class="w-5 h-5 shrink-0 ml-auto text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </button>
      </div>

      <div v-if="households.length > 0" class="my-1" style="height: 1px; background: var(--border-subtle);"></div>

      <!-- Manage Households -->
      <button @click="openHouseholdManager" class="w-full flex items-center gap-3 px-4 py-3 border-0 cursor-pointer transition-all duration-200 text-left touch-manipulation [-webkit-tap-highlight-color:transparent] min-h-[44px]" style="background: transparent; color: var(--accent); font-weight: 500;">
        <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        <span>{{ $t('household.manage_households') }}</span>
      </button>
    </div>
  </Teleport>

  <!-- Desktop: Regular dropdown -->
  <div v-if="showDropdown && !isMobile" class="context-dropdown" v-click-outside="closeDropdown" @click.stop>
    <!-- Personal Budget -->
    <button
      @click="switchToPersonal"
      class="context-dropdown-item"
      :class="{ 'active': isPersonalContext }"
    >
      <span class="pot-dot pot-dot--mitt" aria-hidden="true"></span>
      <span>{{ $t('household.personal_budget') }}</span>
      <svg v-if="isPersonalContext" class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
    </button>

    <div v-if="households.length > 0" class="context-dropdown-divider"></div>

    <!-- Household Budgets -->
    <div v-for="household in households" :key="household.id">
      <button
        @click="switchToHousehold(household.id)"
        class="context-dropdown-item"
        :class="{ 'active': isActiveHousehold(household.id) }"
      >
        <AppAvatar
          v-if="household.imageId"
          size="sm"
          :image-id="household.imageId"
          :name="household.name"
          :alt="household.name"
        />
        <span v-else class="pot-dot pot-dot--felles" aria-hidden="true"></span>
        <div class="household-info">
          <span>{{ household.name }}</span>
          <small>{{ household.members.length }} {{ household.members.length === 1 ? $t('household.member') : $t('household.members_word') }}</small>
        </div>
        <svg v-if="isActiveHousehold(household.id)" class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </button>
    </div>

    <div v-if="households.length > 0" class="context-dropdown-divider"></div>

    <!-- Manage Households -->
    <button @click="openHouseholdManager" class="context-dropdown-item manage-btn">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
      </svg>
      <span>{{ $t('household.manage_households') }}</span>
    </button>
  </div>

</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "../composables/useToast"
import { currentUser } from "../stores/authStore"
import {
  householdStore,
  isPersonalContext,
  switchToHouseholdContext,
  switchToPersonalContext,
} from "../stores/householdStore"
import { logger } from "../utils/logger"
import { AppAvatar, AppImage } from "./ui"

const { t } = useI18n()
const toast = useToast()

const emit = defineEmits<{
  (e: "navigate", tab: string): void
}>()

const showDropdown = ref(false)
const buttonRef = ref<HTMLElement | null>(null)
const dropdownStyle = ref({})

// window.innerWidth isn't reactive, so a plain computed would cache the first
// value forever — track resizes (and orientation changes) explicitly.
const isMobile = ref(window.innerWidth <= 768)
function updateIsMobile() {
  isMobile.value = window.innerWidth <= 768
}
onMounted(() => window.addEventListener("resize", updateIsMobile))
onBeforeUnmount(() => window.removeEventListener("resize", updateIsMobile))

const households = computed(() => householdStore.state.households)
const activeContext = computed(() => householdStore.state.activeContext)

const contextLabel = computed(() => {
  if (isPersonalContext.value) {
    return t("household.personal_budget")
  }
  return activeContext.value.householdName || t("household.household_budget")
})

// Resolved from the households list rather than activeContext, which carries
// the id and name but not the photo. Null in personal context by definition.
const activeHouseholdImageId = computed<string | null>(() => {
  if (isPersonalContext.value) return null
  const active = households.value.find((h) => h.id === activeContext.value.householdId)
  return active?.imageId ?? null
})

async function toggleDropdown() {
  showDropdown.value = !showDropdown.value

  if (showDropdown.value && isMobile.value && buttonRef.value) {
    await nextTick()
    const rect = buttonRef.value.getBoundingClientRect()
    dropdownStyle.value = {
      position: "fixed",
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: "var(--z-dropdown)",
    }
  }
}

function closeDropdown() {
  showDropdown.value = false
}

function switchToPersonal() {
  switchToPersonalContext()
  closeDropdown()
}

function switchToHousehold(householdId: string) {
  const user = currentUser.value
  if (!user) return

  try {
    switchToHouseholdContext(householdId, user.uid)
    closeDropdown()
  } catch (err) {
    logger.error("Failed to switch household", err)
    toast.error(t("toasts.household_switch_failed"))
  }
}

function isActiveHousehold(householdId: string): boolean {
  return activeContext.value.householdId === householdId
}

function openHouseholdManager() {
  closeDropdown()
  emit("navigate", "household")
}

// Click outside directive
interface ClickOutsideElement extends HTMLElement {
  clickOutsideEvent?: (event: Event) => void
  touchOutsideEvent?: (event: Event) => void
  clickOutsideTimer?: ReturnType<typeof setTimeout>
}

const vClickOutside = {
  mounted(el: ClickOutsideElement, binding: any) {
    // Delay to prevent immediate closure on mobile. The timer handle is kept
    // on the element so unmounting within the delay cancels it — otherwise a
    // fast open/close would attach document listeners nobody ever removes.
    el.clickOutsideTimer = setTimeout(() => {
      el.clickOutsideTimer = undefined
      el.clickOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value()
        }
      }
      el.touchOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value()
        }
      }
      document.addEventListener("click", el.clickOutsideEvent)
      document.addEventListener("touchstart", el.touchOutsideEvent)
    }, 300)
  },
  unmounted(el: ClickOutsideElement) {
    if (el.clickOutsideTimer) {
      clearTimeout(el.clickOutsideTimer)
    }
    if (el.clickOutsideEvent) {
      document.removeEventListener("click", el.clickOutsideEvent)
    }
    if (el.touchOutsideEvent) {
      document.removeEventListener("touchstart", el.touchOutsideEvent)
    }
  },
}
</script>
